import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { useTribeAuth } from "@/context/TribeAuthContext";
import api from "@/lib/api";

export function TribeSubscriptionSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { refresh } = useTribeAuth();
  const [status, setStatus] = useState("checking");
  const attempts = useRef(0);

  useEffect(() => {
    const sessionId = params.get("session_id");
    if (!sessionId) { setStatus("error"); return; }
    let timer;
    const poll = async () => {
      attempts.current += 1;
      try {
        const res = await api.get(`/tribe/subscription/verify/${sessionId}`);
        if (res.data.subscription_status === "active") {
          await refresh();
          setStatus("active");
          setTimeout(() => navigate("/tribes/dashboard"), 1500);
          return;
        }
      } catch (e) { console.error("Subscription verify poll failed:", e); }
      if (attempts.current >= 8) { setStatus("pending"); return; }
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
  }, [params, navigate, refresh]);

  return (
    <div className="min-h-screen bg-ink flex flex-col items-center justify-center px-6 text-center" data-testid="tribe-sub-success">
      {status === "active" ? (
        <>
          <CheckCircle2 size={44} className="text-gold" strokeWidth={1.3} />
          <h1 className="font-serif text-4xl mt-6">Subscription Active</h1>
          <p className="font-sans text-sm text-[#A19D98] mt-4">Your repository is unlocked. Redirecting to your dashboard…</p>
        </>
      ) : status === "pending" ? (
        <>
          <Loader2 size={40} className="text-gold animate-spin" />
          <h1 className="font-serif text-3xl mt-6">Confirming your subscription…</h1>
          <p className="font-sans text-sm text-[#A19D98] mt-4">This can take a moment. <button onClick={() => navigate("/tribes/dashboard")} className="text-gold hover:underline">Go to dashboard</button></p>
        </>
      ) : status === "error" ? (
        <>
          <XCircle size={40} className="text-red-400" />
          <h1 className="font-serif text-3xl mt-6">Missing session</h1>
          <button onClick={() => navigate("/tribes/dashboard")} className="mt-6 px-6 py-3 bg-gold text-black font-mono text-[11px] tracking-[0.2em] uppercase">Return to Dashboard</button>
        </>
      ) : (
        <>
          <Loader2 size={40} className="text-gold animate-spin" />
          <h1 className="font-serif text-3xl mt-6">Verifying payment…</h1>
        </>
      )}
    </div>
  );
}

export function TribeSubscriptionCancel() {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-ink flex flex-col items-center justify-center px-6 text-center" data-testid="tribe-sub-cancel">
      <XCircle size={40} className="text-[#A19D98]" />
      <h1 className="font-serif text-4xl mt-6">Checkout Cancelled</h1>
      <p className="font-sans text-sm text-[#A19D98] mt-4">No charge was made. You can subscribe anytime.</p>
      <button onClick={() => navigate("/tribes/dashboard")} className="mt-8 px-6 py-3 bg-gold text-black font-mono text-[11px] tracking-[0.2em] uppercase hover:bg-gold-text transition-colors">Return to Dashboard</button>
    </div>
  );
}
